import classnames from 'classnames/bind'
import { differenceInSeconds } from 'date-fns'
import { useTranslation } from 'next-i18next'
import { MouseEvent, useEffect, useRef, useState } from 'react'

// Components
import Icon from '@/assets/icons'
import Lottie from '@/components/common/Lottie'

// Lotties
import hourglassData from '@/assets/lotties/hourglass.json'
import signalData from '@/assets/lotties/signal.json'

// Constants
import { ROOM_LIVE_TYPE } from '@/constants/liveRoom'

// Libs
import { enterRoom } from '@/methods/livemallclub/enterLiveRoom'

// Styles
import styles from './style.module.scss'

const cx = classnames.bind(styles)

type LiveCountdownProps = {
  roomId: string
  startTime: string
  liveTime: string
  liveType: number
  liveSourceType?: number
}

const MAX_LIVE_DURATION = 60 * 60

const getSecondDiff = (time: string, isLive: boolean): number => {
  const now = new Date()

  if (isLive) return differenceInSeconds(now, new Date(time))

  return differenceInSeconds(new Date(time), now)
}

const getTimeDisplay = (seconds: number) => {
  const IN_SECOND_BOUNDARY = 300

  if (seconds < 0) {
    return '0s'
  }

  if (seconds > IN_SECOND_BOUNDARY) {
    const minute = Math.floor(seconds / 60)
    const second = seconds % 60
    return `${minute}m${second}s`
  }
  return `${seconds}s`
}

const LiveCountdown = ({ startTime, roomId, liveTime, liveType, liveSourceType }: LiveCountdownProps) => {
  const { t } = useTranslation()
  const countdownAnimationRef = useRef<any>(null)
  const [timeDiff, setTimeDiff] = useState<number>(0)
  const timeDisplay = getTimeDisplay(timeDiff)
  const isLive = liveType === ROOM_LIVE_TYPE.CURRENT && !!liveTime

  useEffect(() => {
    const updateTimeDiff = () => {
      const newTimeDiff = isLive ? getSecondDiff(liveTime, true) : getSecondDiff(startTime, false)
      setTimeDiff(newTimeDiff)
      return newTimeDiff
    }

    // 初始化
    const initialTimeDiff = updateTimeDiff()
    if (initialTimeDiff < 0 || (isLive && initialTimeDiff >= MAX_LIVE_DURATION)) return

    // 設置計時器
    const interval = setInterval(() => {
      const newTimeDiff = updateTimeDiff()
      if (newTimeDiff <= 0 || (isLive && newTimeDiff >= MAX_LIVE_DURATION)) {
        clearInterval(interval)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [startTime, liveTime, isLive])

  const handleEnterRoom = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    enterRoom({ roomId, liveType: ROOM_LIVE_TYPE.CURRENT, liveSourceType })
  }

  return (
    <button className={cx('live-countdown')} onClick={handleEnterRoom}>
      <div className={cx('live-countdown__time')}>
        {timeDiff > 0 && (
          <div className={cx('live-countdown__time-lottie-wrapper')} ref={countdownAnimationRef}>
            <Lottie lottieRef={countdownAnimationRef} animationData={isLive ? signalData : hourglassData} config={{ loop: true, autoplay: true }} />
          </div>
        )}
        {timeDiff <= 0 && <Icon.HourGlass_Live_Mall_Club />}
        <div className={cx('live-countdown__time-text')}>
          <div className={cx('live-countdown__time-text-countdown')}>{timeDisplay}</div>
          {!isLive && t('Explore_Feed_Live_Item_Live_Countdown_Open')}
        </div>
      </div>
      <span className={cx('live-countdown__enter-button')}>{t('Explore_Feed_Live_Item_Live_Countdown_Enter_Button')}</span>
    </button>
  )
}

export default LiveCountdown
